import { SlideEditorProvider } from "@/components/slides/editor/SlideEditorContext";
import { useGetLiveMobileView } from "@/lib/hooks/slides/editor/useGetLiveMobileView";
import { convertElementsToPixels } from "@/lib/utils/elementConversion";
import slideEditorReducer, { loadSlide } from "@/store/slideEditorSlice";
import uiReducer from "@/store/uiSlice";
import type { SlideFields } from "@/types/Slide";
import type { SlideElement, SlideTemplate } from "@ai-teach/types";
import { getDefaultSlideData } from "@ai-teach/utils";
import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { useMemo } from "react";
import { Provider } from "react-redux";
import undoable from "redux-undo";

/**
 * Renders each template's ACTUAL Live View component against sample slide data, so the picker
 * shows exactly what the student sees rather than an approximation that can drift.
 *
 * Each preview gets its own throwaway store because the Live View components read the open slide
 * from `state.slideEditor.present` — the same path the editor uses.
 */

// The Live View components lay out against the full-size mobile canvas; scale the whole thing
// down rather than asking them to render small, so proportions stay exactly as authored.
const CANVAS_WIDTH = 480;
const CANVAS_HEIGHT = 800;
const PREVIEW_SCALE = 0.28;

/** Display values chosen to exercise each template's main visual elements. */
const SAMPLE_SLIDE_DATA: Partial<Record<SlideTemplate, Record<string, unknown>>> = {
  vocabulary: { word: "apple", syllable: "ap-ple" },
  listenRepeat: { correctAnswer: "It is an apple.", instructionText: "Describe the picture" },
  conversation: {
    text: "A: What is your name?\nB: My name is Sam.",
    correctAnswer: "My name is Sam.",
  },
  audioRevealPrompt: { text: "This is an apple.", correctAnswer: "This is an apple." },
  passageReading: {
    passages: [
      { id: null, text: "The cat sat on the mat.", audio: null, image: null, whitelist: [] },
    ],
    isTextOnly: true,
  },
  soundToSpelling: {
    phoneme: "/æ/",
    questionMode: "multiple_choice",
    correctAnswer: "a",
    options: ["a", "e", "i"],
  },
  spellingToSound: { phoneme: "/æ/" },
};

/**
 * Sample canvas contents, stored as PERCENTAGES exactly as the API returns them. They are
 * converted to pixels before being loaded, because the editor store holds pixel geometry —
 * see SlidePage, which runs the same conversion on a real slide. Skipping it renders every
 * element at a few pixels in the top-left corner.
 *
 * Only the freeform canvas draws elements; every other template renders from `slideData` alone.
 */
const SAMPLE_ELEMENTS: Partial<Record<SlideTemplate, Record<string, unknown>[]>> = {
  freeformCanvas: [
    {
      id: "preview-circle",
      type: "shape",
      shapeType: "circle",
      position: { x: 10.416666666666668, y: 10.75 },
      size: { width: 41.66666666666667, height: 25 },
      fill: "#1dbddd",
      border: { lineStyle: "none", stroke: "transparent", strokeWidth: 3, cornerRadius: 0 },
      zIndex: 0,
    },
    {
      id: "preview-diamond",
      type: "shape",
      shapeType: "diamond",
      position: { x: 56.041666666666664, y: 24.5 },
      size: { width: 37.5, height: 22.5 },
      fill: "#ddb627",
      border: { lineStyle: "none", stroke: "transparent", strokeWidth: 3, cornerRadius: 0 },
      zIndex: 0,
    },
    {
      id: "preview-arrow",
      type: "shape",
      shapeType: "double-arrow",
      position: { x: 9.375, y: 40.25 },
      size: { width: 50, height: 15 },
      fill: "#9c02e3",
      border: { lineStyle: "none", stroke: "transparent", strokeWidth: 3, cornerRadius: 0 },
      zIndex: 0,
    },
    {
      id: "preview-text-choice",
      type: "textChoice",
      text: "Your answer",
      isCorrectAnswer: false,
      position: { x: 47.083333333333336, y: 59.62499999999999 },
      size: { width: 41.66666666666667, height: 7.5 },
      border: { lineStyle: "none", stroke: "#000000", strokeWidth: 3, cornerRadius: 13 },
      backgroundColor: "#FFFFFF",
      style: {
        color: "#000000",
        textAlign: "center",
        fontFamily: "Comic Sans MS",
        fontStyle: "none",
        textDecoration: "none",
        fontSize: 23,
        fontWeight: "normal",
      },
      zIndex: 0,
    },
    {
      id: "preview-image-choice",
      type: "imageChoice",
      isCorrectAnswer: false,
      position: { x: 13.125, y: 62.375 },
      size: { width: 29.166666666666668, height: 17.5 },
      border: { lineStyle: "none", stroke: "#000000", strokeWidth: 3, cornerRadius: 25 },
      backgroundColor: "#FFFFFF",
      media: { src: null, filename: null, id: null },
      zIndex: 0,
    },
    {
      id: "preview-triangle",
      type: "shape",
      shapeType: "triangle",
      position: { x: 54.37499999999999, y: 72.875 },
      size: { width: 26.041666666666668, height: 13.625000000000002 },
      fill: "#c60606",
      border: { lineStyle: "none", stroke: "transparent", strokeWidth: 3, cornerRadius: 0 },
      zIndex: 0,
    },
  ],
};

function buildSampleSlide(template: SlideTemplate): SlideFields {
  const slideData = {
    ...(getDefaultSlideData(template) as Record<string, unknown>),
    ...(SAMPLE_SLIDE_DATA[template] ?? {}),
  };

  // Cast: the Live View components read only `slideData`, `elements` and the template, so a
  // partial stand-in is enough — this never reaches the API.
  return {
    id: `preview-${template}`,
    name: "Preview",
    lessonId: "preview",
    template,
    elements: convertElementsToPixels(
      (SAMPLE_ELEMENTS[template] ?? []) as unknown as SlideElement[],
      template
    ),
    slideData,
  } as unknown as SlideFields;
}

function makePreviewStore(template: SlideTemplate) {
  const store = configureStore({
    reducer: combineReducers({ slideEditor: undoable(slideEditorReducer), ui: uiReducer }),
    // Sample slideData is intentionally loose; skip the serializability scan for it.
    middleware: (getDefault) => getDefault({ serializableCheck: false }),
  });
  store.dispatch(loadSlide(buildSampleSlide(template)));
  return store;
}

/** Separate component so the hook runs inside the scoped Provider. */
function LiveView() {
  return useGetLiveMobileView({ isStaticPreview: true });
}

export function TemplatePreview({ template }: { template: SlideTemplate }) {
  const store = useMemo(() => makePreviewStore(template), [template]);

  return (
    <div
      className="relative overflow-hidden rounded-xl bg-background ring-1 ring-black/5 dark:ring-white/10 shadow-sm transition-shadow group-hover:shadow-md mx-auto pointer-events-none select-none"
      style={{ width: CANVAS_WIDTH * PREVIEW_SCALE, height: CANVAS_HEIGHT * PREVIEW_SCALE }}
      aria-hidden
    >
      <div
        style={{
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
          transform: `scale(${PREVIEW_SCALE})`,
          transformOrigin: "top left",
        }}
      >
        <Provider store={store}>
          <SlideEditorProvider>
            <LiveView />
          </SlideEditorProvider>
        </Provider>
      </div>
    </div>
  );
}
